import { BS_IMAGE_FIGURE_TYPE, BS_IMAGE_IMG_TYPE } from './constants';
import { resolveBsImageFigure } from './utils';

const SYNCABLE_PROPERTIES = [
    'width',
    'height',
    'min-height',
    'max-height',
    'min-width',
    'max-width',
    'margin',
    'margin-top',
    'margin-right',
    'margin-bottom',
    'margin-left',
    'float',
    'display',
    'position',
    'top',
    'left',
    'right',
    'bottom',
    'align-self',
    'justify-self',
    'flex',
    'flex-grow',
    'flex-shrink',
    'flex-basis',
    'order',
    'z-index',
];

function getComponentSelector(component) {
    if (!component) return '';

    if (typeof component.getSelectorsString === 'function') {
        const selector = component.getSelectorsString();
        if (selector) return selector.trim();
    }

    const id = typeof component.getId === 'function' ? component.getId() : '';
    return id ? `#${id}` : '';
}

function getRuleMedia(rule) {
    if (!rule) return '';

    if (typeof rule.getAtRule === 'function') {
        return rule.getAtRule() || '';
    }

    return rule.get?.('atRule') || rule.get?.('mediaText') || '';
}

function pickSyncableStyles(style = {}) {
    return SYNCABLE_PROPERTIES.reduce((acc, property) => {
        const value = style[property];
        if (value != null && value !== '') {
            acc[property] = value;
        }
        return acc;
    }, {});
}

function findRuleForSelector(cssComposer, selector, media) {
    if (!cssComposer || !selector) return null;

    const rules = cssComposer.getAll?.() || [];
    return rules.find((rule) => {
        const ruleSelector = rule.getSelectorsString?.()?.trim() || '';
        if (ruleSelector !== selector) return false;
        return getRuleMedia(rule) === media;
    }) || null;
}

function upsertRuleForSelector(cssComposer, selector, style, media) {
    if (!cssComposer || !selector || !Object.keys(style).length) return;

    const existingRule = findRuleForSelector(cssComposer, selector, media);
    if (existingRule) {
        existingRule.addStyle(style);
        return;
    }

    const newRule = cssComposer.add(selector, style);
    if (media && newRule?.setAtRule) {
        newRule.setAtRule(media);
    }
}

export function syncFigureStylesFromImage(editor, imageModel) {
    if (!editor || !imageModel || imageModel.get('type') !== BS_IMAGE_IMG_TYPE) return;

    const figure = resolveBsImageFigure(imageModel);
    if (!figure) return;

    const inlineStyle = pickSyncableStyles(imageModel.getStyle?.() || {});
    if (Object.keys(inlineStyle).length) {
        figure.addStyle(inlineStyle);
    }

    const cssComposer = editor.CssComposer;
    const imageSelector = getComponentSelector(imageModel);
    const figureSelector = getComponentSelector(figure);
    if (!cssComposer || !imageSelector || !figureSelector) return;

    const rules = cssComposer.getAll?.() || [];
    rules.forEach((rule) => {
        const ruleSelector = rule.getSelectorsString?.()?.trim() || '';
        if (ruleSelector !== imageSelector) return;

        const syncedStyle = pickSyncableStyles(rule.getStyle?.() || {});
        if (!Object.keys(syncedStyle).length) return;

        upsertRuleForSelector(
            cssComposer,
            figureSelector,
            syncedStyle,
            getRuleMedia(rule)
        );
    });
}

export function syncAllFigureStyles(editor) {
    const wrapper = editor.getWrapper?.();
    if (!wrapper) return;

    wrapper.onAll((component) => {
        if (component.get('type') !== BS_IMAGE_IMG_TYPE) return;
        syncFigureStylesFromImage(editor, component);
    });
}

export function initImageFigureStyleSync(imageModel) {
    const editor = imageModel.em?.get?.('Editor');
    if (!editor) return;

    const sync = () => syncFigureStylesFromImage(editor, imageModel);

    imageModel.on('change:style', sync);
    imageModel.on('change:attributes', sync);
}
